import React, { useState, useMemo, useEffect } from 'react'
import moment from 'moment'
import * as yup from 'yup'
import { Box, Text, Flex, Icon } from '@fundamentals'
import { yupResolver } from '@hookform/resolvers/yup'
import { useToast } from '@core/toast'
import { useGetChecklistById } from '@core/react-query/features/checklists/checklist'
import { Controller, useForm } from 'react-hook-form'
import { ChecklistTemplateRole } from '@core/entities/checklist/ChecklistTemplateRoles'
import { localizedStrings } from '@core/strings'
import {
  TextInput,
  Card,
  IconButton,
  LoadingButton,
  Button,
  Avatar,
} from '@common/material'
import { ChecklistRolePicker } from '@elements/Pickers/ChecklistRolePicker'
import CaptureSignature from '@common/CaptureSignature'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import { useCollectBriefingSignatures } from '@core/react-query/features/briefing'
import { useGetProfile } from '@core/react-query/features/profile'
import { useRouter } from 'next/router'
import { UserAvatar } from '@common/UserAvatar'

type MobileBriefingProps = {
  checklistId: number
  accessToken: string
  validUntil: string
}

type FormData = {
  role: Pick<ChecklistTemplateRole, 'id' | 'name' | 'briefingText'>
  firstName: string
  lastName: string
  company: string
}

const roleSchema = yup.object().shape({
  role: yup.object().required(),
})

const fullSchema = yup.object().shape({
  role: yup.object().required(),
  firstName: yup.string().required().trim(),
  lastName: yup.string().required().trim(),
  company: yup.string().required().trim(),
})

export const MobileBriefing: React.FC<MobileBriefingProps> = ({
  checklistId,
  accessToken,
  validUntil,
}) => {
  const toast = useToast()
  const router = useRouter()

  const accessTokenIsValid = validUntil
    ? moment(validUntil).isAfter(moment())
    : true

  const [signature, setSignature] = useState(null)
  const [pageState, setPageState] = useState<
    'selecting' | 'signing' | 'submitted'
  >('selecting')

  const { data: profile, isFetching: isFetchingProfile } = useGetProfile()
  const { data: checklist, isFetching: isFetchingChecklist } =
    useGetChecklistById(
      { checklistId, accessToken },
      {
        enabled: !!accessToken,
        onError: (error) => toast.error(error.message),
      },
    )

  const { isLoading, mutate: collectBriefingSignatures } =
    useCollectBriefingSignatures()

  const [startedOn] = useState(new Date().toISOString())
  const { control, watch, formState, getValues, handleSubmit, setValue } =
    useForm<FormData>({
      resolver: yupResolver(profile ? roleSchema : fullSchema),
    })

  const { role } = watch()

  // If participant is found -> the user was already briefed onto this checklist
  const participant = useMemo(
    () =>
      profile &&
      checklist
        ?.getActiveParticipants()
        ?.find((participant) => participant.userId === profile.id),
    [checklist, profile],
  )

  useEffect(() => {
    if (participant) {
      const participantRole = participant.roles[0]

      if (participantRole && participantRole.roleId !== role?.id) {
        setValue('role', {
          id: participantRole.roleId,
          name: participantRole.name,
          briefingText: participantRole.briefingText,
        })
      }
    }
  }, [participant, role?.id, setValue])

  const onSubmit = () => {
    const { role, firstName, lastName, company } = getValues()

    const participantData = profile
      ? {
          participantId: participant?.id,
          company: profile.company,
          firstName: profile.firstName,
          lastName: profile.lastName,
          userId: profile.id,
          briefOnRoleId: role.id,
          signature,
        }
      : {
          company,
          firstName,
          lastName,
          briefOnRoleId: role.id,
          signature,
        }

    collectBriefingSignatures(
      {
        accessToken,
        organisationId: checklist?.organisationId,
        referenceObjectId: checklistId,
        referenceObjectType: 'CHECKLIST',
        participants: [participantData],
        briefingPeriod: { startedOn, endedOn: new Date().toISOString() },
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.participantBriefed)
          setPageState('submitted')
        },
        onError: (error) => {
          toast.error(error.message ?? localizedStrings.genericError)
        },
      },
    )
  }

  const handleLoginClick = () => {
    router.push(`/login?redirect=${encodeURIComponent(router.asPath)}`)
  }

  return (
    <Box
      data-test='mobile-briefing'
      display='flex'
      flexDirection='column'
      flex={1}
    >
      <Box
        height='10vh'
        bgcolor='primary.main'
        display='flex'
        justifyContent='center'
        alignItems='center'
      >
        {pageState === 'signing' && (
          <IconButton
            style={{ position: 'absolute', left: 10 }}
            onClick={() => setPageState('selecting')}
          >
            <Icon icon='chevron-left' color='white' />
          </IconButton>
        )}
        <Text fontSize={15} color='white'>
          {localizedStrings.briefingAttendanceRecord}
        </Text>
      </Box>

      {accessTokenIsValid ? (
        <Box m={2} display='flex' flexDirection='column' gap={2}>
          {pageState !== 'submitted' && (
            <>
              <Box>
                <Text fontSize={18} fontWeight={500} mb={0.5}>
                  {localizedStrings.briefingAttendanceRecord}
                </Text>
                <Text color='grey' fontSize='fontSizes.s'>
                  {pageState === 'selecting'
                    ? profile
                      ? localizedStrings.briefingAttendanceRecordDescription
                      : localizedStrings.briefingAttendanceRecordExternalDescription
                    : localizedStrings.briefingAttendanceRecordExternalSigning}
                </Text>
              </Box>

              {!!profile && (
                <Card sx={{ p: 2 }} data-test='user-profile-card'>
                  <Flex alignItems='center' gap={1.5}>
                    <UserAvatar user={profile} sx={{ width: 54, height: 54 }} />
                    <Box flex={1}>
                      <Text fontSize={18} fontWeight={500}>
                        {`${profile.firstName} ${profile.lastName}`}
                      </Text>
                      <Text color='grey'>{profile.company}</Text>
                    </Box>
                  </Flex>
                </Card>
              )}

              {!profile && pageState === 'selecting' && (
                <Card sx={{ p: 2 }}>
                  <Flex alignItems='center' gap={1.5}>
                    <Avatar sx={{ bgcolor: 'silver', width: 54, height: 54 }} />
                    <Flex flexDirection='column' alignItems='start' gap={0.5}>
                      <Text>{localizedStrings.wouldYouLikeToLogIn}</Text>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={handleLoginClick}
                      >
                        {localizedStrings.login}
                      </Button>
                    </Flex>
                  </Flex>
                </Card>
              )}
            </>
          )}

          {pageState === 'selecting' && (
            <Flex flexDirection='column' gap={2}>
              <Controller
                control={control}
                name='role'
                render={({ field }) => (
                  <ChecklistRolePicker
                    checklistId={checklistId}
                    profileGroups={profile?.groups ?? []}
                    accessToken={accessToken}
                    value={field.value}
                    onChange={field.onChange}
                    readOnly={!!participant}
                  />
                )}
              />

              {!profile && (
                <>
                  <Controller
                    control={control}
                    name='firstName'
                    render={({ field }) => (
                      <TextInput
                        data-test='external-briefing-first-name-input'
                        label={localizedStrings.firstName}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name='lastName'
                    render={({ field }) => (
                      <TextInput
                        data-test='external-briefing-last-name-input'
                        label={localizedStrings.lastName}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name='company'
                    render={({ field }) => (
                      <TextInput
                        data-test='external-briefing-company-input'
                        label={localizedStrings.company}
                        {...field}
                      />
                    )}
                  />
                </>
              )}
            </Flex>
          )}

          {pageState === 'signing' && (
            <Flex flexDirection='column'>
              {role?.briefingText && (
                <Card>
                  <Text
                    dangerouslySetInnerHTML={{ __html: role.briefingText }}
                    p={2}
                  />
                </Card>
              )}
              <CaptureSignature inputProps={{ onChange: setSignature }} />
            </Flex>
          )}

          {pageState === 'submitted' && (
            <Flex justifyContent='center'>
              <Text p={2} data-test='participant-briefed'>
                {localizedStrings.participantBriefed}
              </Text>
              <Icon icon='check' />
            </Flex>
          )}

          {pageState !== 'submitted' && (
            <LoadingButton
              data-test='external-briefing-submit-button'
              loading={isLoading}
              fullWidth
              variant='contained'
              disabled={
                pageState === 'signing'
                  ? !signature
                  : profile
                  ? !role
                  : !formState.isValid
              }
              onClick={
                pageState === 'selecting'
                  ? () => setPageState('signing')
                  : handleSubmit(onSubmit)
              }
            >
              {pageState === 'selecting'
                ? localizedStrings.next
                : localizedStrings.submit}
            </LoadingButton>
          )}

          <BackdropLoader
            open={isLoading || isFetchingProfile || isFetchingChecklist}
          />
        </Box>
      ) : (
        <Box
          display='flex'
          flex={1}
          m={2}
          justifyContent='center'
          alignItems='center'
        >
          <Text
            fontSize={16}
            textAlign='center'
            fontWeight={500}
            whiteSpace='pre-line'
          >
            {localizedStrings.qrCodeExpired}
          </Text>
        </Box>
      )}
    </Box>
  )
}
